import { deleteComment, putRateComment } from "@api/comments";
import { CommentRating, type TracklistComment } from "@api/comments/types";
import ConfirmDelete from "@components/ConfirmDelete";
import FadedText from "@components/FadedText";
import { Column, LineClamp, Row } from "@components/Layout";
import { toast } from "@components/Toaster";
import UserIcon from "@components/UserIcon";
import Waves from "@components/Waves";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import { userContext } from "@layouts/App/context/User";
import { formatPlayTime, formatTime, trimString } from "@src/app/utils";
import { FaRegularTrashCan, FaSolidHeadphones } from "solid-icons/fa";
import { RiSystemThumbDownFill, RiSystemThumbDownLine, RiSystemThumbUpFill, RiSystemThumbUpLine } from "solid-icons/ri";
import { Show, type VoidComponent, createMemo, createSignal, useContext } from "solid-js";
import { CommentWrapper } from "./styled";

const CommentRow: VoidComponent<{
	comment: TracklistComment;
	even: boolean;
	index: number;
	reversed: boolean;
	total: number;
	onRating: (rating: CommentRating) => void;
	onDelete: () => void;
	onPlay: () => void;
}> = (props) => {
	const { user } = useContext(userContext);
	const [loading, setLoading] = createSignal(false);
	const [confirm, setConfirm] = createSignal(false);
	const { t } = useI18n();

	const index = createMemo(() => {
		if (!props.reversed) return props.index + 1;
		return props.total - props.index;
	});

	const positiveRating =
		props.comment.rating.positive - (props.comment.rating.user_choice === CommentRating.POSITIVE ? 1 : 0);

	const negativeRating =
		props.comment.rating.negative - (props.comment.rating.user_choice === CommentRating.NEGATIVE ? 1 : 0);

	const handleRateComment = (rating: CommentRating) => {
		setLoading(true);
		const _rating = rating === props.comment.rating.user_choice ? CommentRating.NONE : rating;
		putRateComment(props.comment.uuid, _rating)
			.then(() => props.onRating(_rating))
			.finally(() => setLoading(false));
	};

	const handleDeleteComment = () => {
		const p = deleteComment(props.comment.uuid);
		toast.promise(p, {
			loading: t("global.deleting.loading"),
			success: () => t("global.deleting.success"),
			error: () => t("global.deleting.error"),
		});
		p.then(props.onDelete).finally(() => setConfirm(false));
	};

	return (
		<CommentWrapper even={props.even} padding="20px" gap="30px">
			<Row gap="5px">
				<Column w="max-content">
					<UserIcon diameter="40px" src={props.comment.author.photo} />
				</Column>
				<Column gap="30px">
					<Row justifyContent="space-between" alignItems="center">
						<h4 class={LineClamp}>
							{index()}. {trimString(props.comment.author.nickname, 30)}
						</h4>
						<Row w="max-content" flexShrink={0}>
							<FadedText fontSize="14px">
								<Row gap="5px">
									<FaSolidHeadphones />
									{formatPlayTime(props.comment.length, { s: true })}
								</Row>
							</FadedText>
						</Row>
					</Row>
					<Row gap="10px" alignItems="center">
						<PlayButton diameter="30px" onClick={props.onPlay} track={{ id: props.comment.uuid, index: 0 }} />
						<Waves h="25px" count={6} progress={0} seed={props.comment.uuid} />
						<Show when={props.comment.author.uid === user.uid}>
							<Column w="30px">
								<ConfirmDelete
									confirmText={t("app.comment.delete")}
									onConfirm={handleDeleteComment}
									open={confirm()}
									onOpenChange={setConfirm}
								>
									<FadedText>
										<FaRegularTrashCan />
									</FadedText>
								</ConfirmDelete>
							</Column>
						</Show>
					</Row>
				</Column>
			</Row>
			<Row gap="5px" alignItems="center" justifyContent="space-between">
				<Row w="max-content" gap="5px">
					<button onclick={() => handleRateComment(CommentRating.POSITIVE)} disabled={loading()} type="button">
						<FadedText>
							<Row w="max-content" gap="5px" alignItems="center">
								<Show
									when={props.comment.rating.user_choice === CommentRating.POSITIVE}
									fallback={<RiSystemThumbUpLine />}
								>
									<RiSystemThumbUpFill />
								</Show>
								{positiveRating + (props.comment.rating.user_choice === CommentRating.POSITIVE ? 1 : 0)}
							</Row>
						</FadedText>
					</button>
					<button onclick={() => handleRateComment(CommentRating.NEGATIVE)} disabled={loading()} type="button">
						<FadedText>
							<Row w="max-content" gap="5px" alignItems="center">
								<Show
									when={props.comment.rating.user_choice === CommentRating.NEGATIVE}
									fallback={<RiSystemThumbDownLine />}
								>
									<RiSystemThumbDownFill />
								</Show>
								{negativeRating + (props.comment.rating.user_choice === CommentRating.NEGATIVE ? 1 : 0)}
							</Row>
						</FadedText>
					</button>
				</Row>
				<FadedText fontSize="13px">{formatTime(props.comment.created_at)}</FadedText>
			</Row>
		</CommentWrapper>
	);
};
export default CommentRow;
